import React, { useState, useEffect } from 'react';
import { authorizedFetch } from '../utils/api';
import {
    ArrowTrendingUpIcon,
    SparklesIcon,
    FireIcon,
    EyeIcon,
    CalendarIcon,
    SwatchIcon,
    TagIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';

const Trends = () => {
    const [trendsData, setTrendsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [timeRange, setTimeRange] = useState('30d');
    const [trendType, setTrendType] = useState('rising');
    const [renderTime, setRenderTime] = useState(null);
    const [showRenderTime, setShowRenderTime] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [brandScoreData, setBrandScoreData] = useState(null);
    const [loadingBrandData, setLoadingBrandData] = useState(false);
    const [showScorePlot, setShowScorePlot] = useState(false);
    const [fashionTrendsData, setFashionTrendsData] = useState(null);
    const [loadingFashionTrends, setLoadingFashionTrends] = useState(false);
    const [showFashionTrendsModal, setShowFashionTrendsModal] = useState(false);
    const renderStartRef = React.useRef(null);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        fetchTrendsData();
        fetchFashionTrendsData();
    }, [timeRange, selectedCategory, trendType]);

    const fetchFashionTrendsData = async () => {
        setLoadingFashionTrends(true);
        try {
            const response = await authorizedFetch(`${backendUrl}/fashion-trends/?limit=20`);
            if (response.ok) {
                const data = await response.json();
                setFashionTrendsData(data);
            } else {
                console.error('Failed to fetch fashion trends data');
            }
        } catch (err) {
            console.error('Error fetching fashion trends data:', err);
        } finally {
            setLoadingFashionTrends(false);
        }
    };

    const fetchBrandScoreData = async (brandName) => {
        setLoadingBrandData(true);
        try {
            const response = await authorizedFetch(`${backendUrl}/trends/brand-scores/${encodeURIComponent(brandName)}`);
            if (response.ok) {
                const data = await response.json();
                setBrandScoreData(data);
                setSelectedBrand(brandName);
                setShowScorePlot(true);
            } else {
                console.error('Failed to fetch brand score data');
            }
        } catch (err) {
            console.error('Error fetching brand score data:', err);
        } finally {
            setLoadingBrandData(false);
        }
    };

    const fetchTrendsData = async () => {
        renderStartRef.current = performance.now();
        setLoading(true);
        setError(null);

        try {
            const [trendingLooks, brandTrends, seasonalTrends, colorTrends, predictions] = await Promise.all([
                authorizedFetch(`${backendUrl}/trends/looks?range=${timeRange}&category=${selectedCategory}&type=${trendType}`),
                authorizedFetch(`${backendUrl}/trends/brands?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/trends/seasonal?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/trends/colors?range=${timeRange}`),
                authorizedFetch(`${backendUrl}/trends/predictions?range=${timeRange}`)
            ]);

            const data = {
                trendingLooks: trendingLooks.ok ? await trendingLooks.json() : [],
                brandTrends: brandTrends.ok ? await brandTrends.json() : [],
                seasonalTrends: seasonalTrends.ok ? await seasonalTrends.json() : [],
                colorTrends: colorTrends.ok ? await colorTrends.json() : [],
                predictions: predictions.ok ? await predictions.json() : []
            };

            setTrendsData(data);

            // Performance tracking
            const end = performance.now();
            const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
            setRenderTime(seconds);
            setShowRenderTime(true);
            setTimeout(() => setShowRenderTime(false), 5000);

        } catch (err) {
            console.error('Error fetching trends:', err);
            setError('Failed to load trends data');
        } finally {
            setLoading(false);
        }
    };

    // Loading Skeleton
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
                            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-500 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                        <div className="space-y-3">
                            {[...Array(5)].map((_, j) => (
                                <div key={j} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // Trending Look Card
    const TrendingLookCard = ({ look, rank }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative">
                <img
                    src={look.image_url}
                    alt={`${look.brand} Look ${look.index}`}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                        e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
                    }}
                />
                <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                    #{rank}
                </div>
                <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                    {look.avg_score}/10
                </div>
                {look.trend_indicator && (
                    <div className="absolute bottom-2 left-2 flex items-center space-x-1 bg-green-500 text-white px-2 py-1 rounded text-xs">
                        <ArrowTrendingUpIcon className="h-3 w-3" />
                        <span>{look.trend_indicator}</span>
                    </div>
                )}
            </div>
            <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {look.brand}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {look.designer} • {look.season} {look.year}
                </p>
                <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                    <span>{look.total_ratings} ratings</span>
                    <span className="flex items-center space-x-1">
                        <FireIcon className="h-3 w-3" />
                        <span>{look.popularity_score}% popular</span>
                    </span>
                </div>
            </div>
        </div>
    );

    // Score Plot Modal Component
    const ScorePlotModal = () => {
        if (!showScorePlot || !brandScoreData) return null;

        const maxScore = Math.max(...brandScoreData.collections.map(c => c.avg_score));
        const minScore = Math.min(...brandScoreData.collections.map(c => c.avg_score));
        const scoreRange = maxScore - minScore || 1;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {selectedBrand} - Collection Scores
                            </h2>
                            <button
                                onClick={() => setShowScorePlot(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Summary Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {brandScoreData.collections.length}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Collections</div>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    {brandScoreData.overall_avg.toFixed(1)}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Overall Avg</div>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                    {maxScore.toFixed(1)}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Highest Score</div>
                            </div>
                            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                    {brandScoreData.total_looks}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">Total Looks</div>
                            </div>
                        </div>

                        {/* Score Plot */}
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                    Collection Performance Over Time
                                </h3>
                                <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded"></div>
                                        <span>Trend Line</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-md"></div>
                                        <span>Score Points</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 bg-green-500 rounded opacity-30"></div>
                                        <span>Collection Bars</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                {/* Y-axis labels */}
                                <div className="absolute left-0 top-0 bottom-0 w-16 flex flex-col justify-between text-sm text-gray-500 dark:text-gray-400 font-medium">
                                    <span>10.0</span>
                                    <span>8.0</span>
                                    <span>6.0</span>
                                    <span>4.0</span>
                                    <span>2.0</span>
                                    <span>0.0</span>
                                </div>

                                {/* Plot area */}
                                <div className="ml-16 relative h-96 bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-inner">
                                    {/* Grid lines */}
                                    {[0, 2, 4, 6, 8, 10].map(score => (
                                        <div
                                            key={score}
                                            className="absolute w-full border-t border-gray-200 dark:border-gray-600"
                                            style={{ bottom: `${(score / 10) * 100}%` }}
                                        />
                                    ))}

                                    {/* Connected line chart with circles */}
                                    <div className="relative h-full p-4">
                                        <svg
                                            className="absolute inset-0 w-full h-full"
                                            viewBox="0 0 100 100"
                                            preserveAspectRatio="none"
                                        >
                                            <defs>
                                                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9" />
                                                    <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.9" />
                                                    <stop offset="100%" stopColor="#EC4899" stopOpacity="0.9" />
                                                </linearGradient>
                                                <filter id="circleShadow" x="-50%" y="-50%" width="200%" height="200%">
                                                    <feDropShadow dx="0" dy="2" stdDeviation="1" floodOpacity="0.3" />
                                                </filter>
                                            </defs>

                                            {/* Connected line */}
                                            {brandScoreData.collections.length > 1 && (
                                                <polyline
                                                    fill="none"
                                                    stroke="url(#scoreGradient)"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    vectorEffect="non-scaling-stroke"
                                                    points={brandScoreData.collections.map((collection, index) => {
                                                        const x = ((index + 0.5) / brandScoreData.collections.length) * 100;
                                                        const y = 100 - (collection.avg_score / 10) * 100;
                                                        return `${x},${y}`;
                                                    }).join(' ')}
                                                />
                                            )}
                                        </svg>

                                        {/* Interactive circles positioned over the line */}
                                        <div className="absolute inset-0 flex items-end justify-around p-4">
                                            {brandScoreData.collections.map((collection, index) => {
                                                const yPosition = (collection.avg_score / 10) * 100;
                                                const color = collection.avg_score >= 8 ? 'bg-green-500 border-green-600' :
                                                    collection.avg_score >= 6 ? 'bg-yellow-500 border-yellow-600' :
                                                        collection.avg_score >= 4 ? 'bg-orange-500 border-orange-600' : 'bg-red-500 border-red-600';

                                                return (
                                                    <div key={index} className="relative flex-1 flex justify-center group">
                                                        {/* Circle positioned at the correct height */}
                                                        <div
                                                            className={`absolute w-6 h-6 ${color} rounded-full border-2 border-white shadow-lg cursor-pointer transform -translate-x-1/2 transition-all duration-200 hover:scale-125 hover:shadow-xl z-10`}
                                                            style={{ bottom: `${yPosition}%` }}
                                                        >
                                                            {/* Hover tooltip */}
                                                            <div className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg py-3 px-4 whitespace-nowrap z-30 shadow-2xl">
                                                                <div className="font-bold text-lg text-center mb-2">{collection.avg_score.toFixed(1)}/10</div>
                                                                <div className="border-t border-gray-600 pt-2 space-y-1">
                                                                    <div className="font-semibold text-blue-300">{collection.season} {collection.year}</div>
                                                                    <div className="text-green-300">{collection.look_count} looks</div>
                                                                    <div className="text-yellow-300">Designer: {collection.designer}</div>
                                                                    {collection.high_rated > 0 && (
                                                                        <div className="text-purple-300">{collection.high_rated} high-rated (8+)</div>
                                                                    )}
                                                                </div>
                                                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* X-axis label */}
                                <div className="ml-16 mt-4 text-center text-lg font-medium text-gray-700 dark:text-gray-300">
                                    Collections Timeline (Season & Year)
                                </div>
                            </div>
                        </div>

                        {/* Collection Details */}
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Collection Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {brandScoreData.collections.map((collection, index) => (
                                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-medium text-gray-900 dark:text-white">
                                                {collection.season} {collection.year}
                                            </h4>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${collection.avg_score >= 8 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                collection.avg_score >= 6 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                                    collection.avg_score >= 4 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                                                        'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                                }`}>
                                                {collection.avg_score.toFixed(1)}/10
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                            <div>{collection.look_count} looks</div>
                                            <div>Designer: {collection.designer || 'Unknown'}</div>
                                            {collection.high_rated > 0 && (
                                                <div className="text-green-600 dark:text-green-400">
                                                    {collection.high_rated} high-rated (8+)
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Brand Trend Item
    const BrandTrendItem = ({ brand, index }) => (
        <div
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            onClick={() => fetchBrandScoreData(brand.name)}
        >
            <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">
                    #{index + 1}
                </span>
                <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                        {brand.name}
                    </span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        {brand.total_looks} looks • Avg: {brand.avg_score}/10
                    </div>
                </div>
            </div>
            <div className="flex items-center space-x-2">
                <span className={`text-sm font-medium ${brand.trend_direction === 'up' ? 'text-green-600' :
                    brand.trend_direction === 'down' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                    {brand.trend_change > 0 ? '+' : ''}{brand.trend_change}%
                </span>
                <div className={`w-2 h-2 rounded-full ${brand.trend_direction === 'up' ? 'bg-green-500' :
                    brand.trend_direction === 'down' ? 'bg-red-500' : 'bg-gray-500'
                    }`}></div>
                <ChartBarIcon className="h-4 w-4 text-gray-400" />
            </div>
        </div>
    );

    // Color Trend Item
    const ColorTrendItem = ({ color }) => (
        <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div
                className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: color.hex_code || '#gray' }}
            ></div>
            <div className="flex-1">
                <span className="font-medium text-gray-900 dark:text-white">
                    {color.name}
                </span>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                    {color.frequency} appearances • {color.avg_score}/10 avg
                </div>
            </div>
            <div className="text-right">
                <div className={`text-sm font-medium ${color.trend === 'rising' ? 'text-green-600' :
                    color.trend === 'falling' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                    {color.trend === 'rising' ? '↗' : color.trend === 'falling' ? '↘' : '→'}
                </div>
            </div>
        </div>
    );

    // Prediction Card
    const PredictionCard = ({ prediction }) => (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center space-x-2 mb-2">
                <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                <span className="font-medium text-purple-900 dark:text-purple-100">
                    AI Prediction
                </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                {prediction.description}
            </p>
            <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                    Confidence: {prediction.confidence}%
                </span>
                <span className="text-purple-600 dark:text-purple-400">
                    {prediction.category}
                </span>
            </div>
        </div>
    );

    // Fashion Trends Modal Component
    const FashionTrendsModal = () => {
        if (!showFashionTrendsModal) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Fashion Trends Database
                            </h2>
                            <button
                                onClick={() => setShowFashionTrendsModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {loadingFashionTrends ? (
                            <div className="text-center py-8">
                                <div className="inline-flex items-center space-x-2">
                                    <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Loading fashion trends...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {fashionTrendsData && fashionTrendsData.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {fashionTrendsData.map((trend, index) => (
                                            <div key={trend.id || index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h3 className="font-semibold text-gray-900 dark:text-white">
                                                        {trend.trend_name}
                                                    </h3>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${trend.trend_direction === 'rising' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                        trend.trend_direction === 'falling' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                                            trend.trend_direction === 'emerging' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                                'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                                        }`}>
                                                        {trend.trend_direction}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="font-medium">Type:</span>
                                                        <span className="capitalize">{trend.trend_type}</span>
                                                        {trend.hex_code && (
                                                            <div
                                                                className="w-4 h-4 rounded-full border border-gray-300"
                                                                style={{ backgroundColor: trend.hex_code }}
                                                            ></div>
                                                        )}
                                                    </div>
                                                    {trend.category && (
                                                        <div>Category: {trend.category}</div>
                                                    )}
                                                    {trend.season && trend.year && (
                                                        <div>Season: {trend.season} {trend.year}</div>
                                                    )}
                                                </div>
                                                {trend.description && (
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                                        {trend.description}
                                                    </p>
                                                )}
                                                <div className="flex justify-between items-center text-xs">
                                                    {trend.confidence_score && (
                                                        <span className="text-blue-600 dark:text-blue-400">
                                                            Confidence: {trend.confidence_score}%
                                                        </span>
                                                    )}
                                                    {trend.popularity_score && (
                                                        <span className="text-purple-600 dark:text-purple-400">
                                                            Popularity: {trend.popularity_score}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        No fashion trends data available
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Fashion Trend Item Component
    const FashionTrendItem = ({ trend }) => (
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center space-x-3">
                {trend.hex_code && (
                    <div
                        className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: trend.hex_code }}
                    ></div>
                )}
                <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                        {trend.trend_name}
                    </span>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                        {trend.trend_type} • {trend.category || 'General'}
                    </div>
                </div>
            </div>
            <div className="flex items-center space-x-2">
                {trend.confidence_score && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                        {trend.confidence_score}%
                    </span>
                )}
                <div className={`w-2 h-2 rounded-full ${trend.trend_direction === 'rising' ? 'bg-green-500' :
                    trend.trend_direction === 'falling' ? 'bg-red-500' :
                        trend.trend_direction === 'emerging' ? 'bg-blue-500' : 'bg-gray-500'
                    }`}></div>
            </div>
        </div>
    );

    if (loading) return <LoadingSkeleton />;

    if (error) {
        return (
            <div className="p-4 text-center text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <h2 className="text-lg font-semibold mb-2">Error Loading Trends</h2>
                <p>{error}</p>
                <button
                    onClick={fetchTrendsData}
                    className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
            {/* Score Plot Modal */}
            <ScorePlotModal />
            {/* Fashion Trends Modal */}
            <FashionTrendsModal />
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Fashion Trends
                    </h1>
                    <div className="flex items-center space-x-4">
                        <select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="1y">Last year</option>
                        </select>
                        <select
                            value={trendType}
                            onChange={(e) => setTrendType(e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="rising">Rising Trends</option>
                            <option value="hot">Hot Now</option>
                            <option value="emerging">Emerging</option>
                            <option value="declining">Declining</option>
                        </select>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-4 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <ArrowTrendingUpIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                            <div>
                                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                    {trendsData?.trendingLooks?.length || 0}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Trending Looks</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <FireIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                            <div>
                                <div className="text-xl font-bold text-green-600 dark:text-green-400">
                                    {trendsData?.brandTrends?.filter(b => b.trend_direction === 'up').length || 0}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Rising Brands</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <SwatchIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                            <div>
                                <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                    {trendsData?.colorTrends?.length || 0}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Color Trends</div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-4 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <SparklesIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                            <div>
                                <div className="text-xl font-bold text-yellow-600 dark:text-yellow-400">
                                    {trendsData?.predictions?.length || 0}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">AI Predictions</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trending Looks */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center space-x-2">
                    <FireIcon className="h-6 w-6 text-orange-500" />
                    <span>Trending Looks</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {trendsData?.trendingLooks?.slice(0, 8).map((look, index) => (
                        <TrendingLookCard key={look.id} look={look} rank={index + 1} />
                    ))}
                </div>
            </div>

            {/* Brand Trends and Color Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Brand Trends */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center space-x-2">
                        <TagIcon className="h-5 w-5 text-blue-500" />
                        <span>Brand Trends</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                            (Click to view score plots)
                        </span>
                    </h3>
                    <div className="space-y-3">
                        {trendsData?.brandTrends?.slice(0, 8).map((brand, index) => (
                            <BrandTrendItem key={brand.name} brand={brand} index={index} />
                        ))}
                    </div>
                    {loadingBrandData && (
                        <div className="mt-4 text-center text-gray-500 dark:text-gray-400">
                            <div className="inline-flex items-center space-x-2">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Loading brand score data...</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Color Trends */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center space-x-2">
                        <SwatchIcon className="h-5 w-5 text-purple-500" />
                        <span>Color Trends</span>
                    </h3>
                    <div className="space-y-3">
                        {trendsData?.colorTrends?.slice(0, 8).map((color, index) => (
                            <ColorTrendItem key={color.name} color={color} />
                        ))}
                    </div>
                </div>
            </div>

            {/* AI Predictions */}
            {trendsData?.predictions?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center space-x-2">
                        <SparklesIcon className="h-5 w-5 text-purple-500" />
                        <span>AI Fashion Predictions</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {trendsData.predictions.map((prediction, index) => (
                            <PredictionCard key={index} prediction={prediction} />
                        ))}
                    </div>
                </div>
            )}

            {/* Fashion Trends Database */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <EyeIcon className="h-5 w-5 text-indigo-500" />
                        <span>Fashion Trends Database</span>
                    </h3>
                    <button
                        onClick={() => setShowFashionTrendsModal(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                    >
                        View All Trends
                    </button>
                </div>
                <div className="space-y-3">
                    {fashionTrendsData && fashionTrendsData.length > 0 ? (
                        fashionTrendsData.slice(0, 6).map((trend, index) => (
                            <FashionTrendItem key={trend.id || index} trend={trend} />
                        ))
                    ) : (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            {loadingFashionTrends ? (
                                <div className="inline-flex items-center space-x-2">
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Loading fashion trends...</span>
                                </div>
                            ) : (
                                "No fashion trends data available"
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Render Time Display */}
            {showRenderTime && (
                <div className="fixed bottom-6 right-6 bg-gray-800 text-white text-xs px-3 py-1 rounded shadow-lg animate-fade-in z-50">
                    Load Time: {renderTime} seconds
                </div>
            )}
        </div>
    );
};

export default Trends;